/**
 * Centralized Hash Management System
 *
 * This module provides a unified interface for all hash-related operations
 * across the application. All hash changes should go through this system
 * to ensure consistency and easier maintenance.
 *
 * @example Basic usage:
 * ```typescript
 * import { setHash, getCurrentHash, useHash } from '@/hashChange';
 *
 * // Set hash with scroll
 * setHash('section-1', { scroll: true, offset: 80 });
 *
 * // In React component
 * const [hash, setHash] = useHash();
 * ```
 *
 * @example Navigation with hash:
 * ```typescript
 * import { useHashNavigation } from '@/hashChange';
 *
 * const { pushWithHash, replaceWithHash } = useHashNavigation();
 * pushWithHash('step-2');
 * ```
 */

import { clearHash, getCurrentHash, hashElementExists, isCurrentHash, scrollToHash, setHash } from './utils';

// Core system
export { HashManager, hashManager } from './HashManager';

// React hooks
export { useHash, useHashNavigation, useHashScroll, useHashListener, useIsHashActive } from './hooks';

// Utility functions
export {
  cleanHash,
  ensureHashPrefix,
  extractHashFromUrl,
  buildUrlWithHash,
  hashElementExists,
  getElementByHash,
  scrollToHash,
  setHash,
  clearHash,
  getCurrentHash,
  isCurrentHash,
  generateHashId,
  isValidHash,
  getHashFromUrl,
  createHashHandler,
  batchHashOperations,
} from './utils';

// Types
export type {
  HashChangeOptions,
  HashNavigationOptions,
  HashManagerConfig,
  HashChangeHandler,
  HashEventDetails,
} from './types';

// Migration helpers for easy transition
export const hashChange = {
  set: setHash,
  get: getCurrentHash,
  clear: clearHash,
  scroll: scrollToHash,
  exists: hashElementExists,
  isActive: isCurrentHash,
} as const;
